import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ServicesPage = () => {
  const services = [
    {
      title: 'Interior & Exterior Carpentry',
      icon: 'ri-hammer-line',
      description: 'Professional installation of high-quality carpentry for residential and commercial spaces.',
      image: 'https://public.readdy.ai/ai/img_res/a9717b86703b318d29593cd797c3a4d9.jpg',
      features: [
        'Interior and exterior doors',
        'Wooden and PVC windows',
        'Custom built-in wardrobes',
        'Wooden flooring and decking',
        'Wood paneling and cladding',
        'Stairs and railings'
      ]
    },
    {
      title: 'Land Preparation',
      icon: 'ri-truck-line',
      description: 'Earthwork and land conditioning with specialized machinery and qualified personnel.',
      image: 'https://readdy.ai/api/search-image?query=heavy%20excavator%20and%20bulldozer%20machinery%20working%20on%20construction%20site%20preparing%20and%20leveling%20ground%20for%20building%20foundation%20with%20professional%20operators%20in%20organized%20worksite%20showing%20quality%20earthwork%20and%20land%20preparation&width=800&height=600&seq=serv002&orientation=landscape',
      features: [
        'Excavation and earthmoving',
        'Land leveling',
        'Soil compaction',
        'Drainage and channeling',
        'Foundation preparation',
        'Clearing and site cleanup'
      ]
    },
    {
      title: 'Floor & Wall Coverings',
      icon: 'ri-layout-grid-line',
      description: 'Expert installation of all types of coverings with impeccable and lasting finishes.',
      image: 'https://readdy.ai/api/search-image?query=skilled%20craftsman%20installing%20premium%20ceramic%20tiles%20and%20natural%20stone%20on%20modern%20bathroom%20walls%20and%20floors%20with%20precise%20alignment%20perfect%20grout%20lines%20showing%20professional%20tile%20work%20in%20elegant%20contemporary%20space&width=800&height=600&seq=serv003&orientation=landscape',
      features: [
        'Ceramic and porcelain tiles',
        'Parquet and floating floors',
        'Natural and artificial stone',
        'Microcement and resins',
        'Plaster and stucco',
        'Wallpaper and decorative coverings'
      ]
    },
    {
      title: 'Demolition & Structural Preparation',
      icon: 'ri-delete-bin-line',
      description: 'Controlled and safe demolition with strict compliance to safety regulations.',
      image: 'https://readdy.ai/api/search-image?query=controlled%20demolition%20of%20interior%20walls%20in%20commercial%20building%20renovation%20with%20professional%20crew%20wearing%20safety%20equipment%20using%20proper%20tools%20and%20dust%20control%20measures%20in%20organized%20safe%20construction%20environment&width=800&height=600&seq=serv004&orientation=landscape',
      features: [
        'Structural demolition',
        'Partition and wall removal',
        'Debris removal',
        'Opening creation',
        'Structural rehabilitation',
        'Waste management'
      ]
    }
  ];

  const process = [
    {
      number: '01',
      title: 'Initial Consultation',
      description: 'Meeting to understand your needs and project objectives'
    },
    {
      number: '02',
      title: 'Detailed Quote',
      description: 'Complete budget preparation with no hidden costs'
    },
    {
      number: '03',
      title: 'Planning',
      description: 'Work plan design and execution timeline'
    },
    {
      number: '04',
      title: 'Execution',
      description: 'Project implementation with constant supervision'
    },
    {
      number: '05',
      title: 'Delivery',
      description: 'Final inspection and completed project handover'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=wide%20angle%20view%20of%20professional%20construction%20team%20working%20on%20multiple%20tasks%20at%20modern%20building%20site%20showing%20various%20construction%20services%20including%20carpentry%20flooring%20and%20structural%20work%20with%20organized%20workspace%20and%20quality%20equipment&width=1920&height=800&seq=servhero001&orientation=landscape"
            alt="Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm font-bold tracking-widest text-orange uppercase mb-4">
              OUR SERVICES
            </p>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Complete Construction Solutions
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Professional construction and rehabilitation services with quality guarantee
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto space-y-32">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="relative h-96 lg:h-[500px] rounded-lg overflow-hidden shadow-2xl">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="w-16 h-16 flex items-center justify-center border-2 border-orange rounded">
                  <i className={`${service.icon} text-3xl text-orange`}></i>
                </div>
                <h2 className="text-4xl lg:text-5xl font-bold text-charcoal">
                  {service.title}
                </h2>
                <p className="text-lg text-textGrey leading-relaxed">
                  {service.description}
                </p>
                <div className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <i className="ri-check-line text-orange text-xl"></i>
                      <span className="text-textGrey">{feature}</span>
                    </div>
                  ))}
                </div>
                <a
                  href="#contact"
                  className="inline-flex items-center space-x-2 bg-charcoal text-white px-8 py-4 font-semibold text-base tracking-wide hover:bg-charcoal/90 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span>REQUEST A QUOTE</span>
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-concrete py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm font-bold tracking-widest text-orange uppercase mb-4">
              OUR PROCESS
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold text-charcoal mb-6">
              How We Work
            </h2>
            <p className="text-lg text-textGrey max-w-3xl mx-auto">
              A transparent and efficient process to ensure your project's success
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-white p-8 rounded-lg shadow-lg h-full">
                  <div className="text-6xl font-bold text-orange/20 mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold text-charcoal mb-3">
                    {step.title}
                  </h3>
                  <p className="text-textGrey leading-relaxed">
                    {step.description}
                  </p>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <i className="ri-arrow-right-line text-2xl text-orange"></i>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-24 px-6 lg:px-12 bg-charcoal">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Need More Information?
            </h2>
            <p className="text-xl text-white/90 mb-10">
              Contact us for a personalized quote
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <a
                href="tel:+34912345678"
                className="inline-flex items-center justify-center space-x-2 bg-orange text-white px-8 py-4 font-semibold text-base tracking-wide hover:bg-orange/90 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl"></i>
                <span>+34 912 345 678</span>
              </a>
              <a
                href="mailto:info@levantina.org"
                className="inline-flex items-center justify-center space-x-2 bg-transparent text-white px-8 py-4 font-semibold text-base tracking-wide border-2 border-white hover:bg-white hover:text-charcoal transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line text-xl"></i>
                <span>info@levantina.org</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
